import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  getAccessLevelFromGroups,
  getDefaultRouteForSession,
  storeOrquestadorSession,
} from '../auth/orquestadorAccess';
import {
  authenticateOrquestadorUser,
  markOrquestadorSessionValidatedForCurrentPageLoad,
} from '../auth/orquestadorAuthApi';
import {
  fetchOrquestadorUpdatedAtLabel,
  getCachedOrquestadorUpdatedAtLabel,
} from '../utils/orquestadorUpdatedAt';
import './Login.css';

const REMEMBERED_CREDENTIALS_STORAGE_KEY = 'orquestadorMahouRememberedCredentials';

const Login = () => {
  const history = useHistory();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordHoverLocked, setIsPasswordHoverLocked] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberPassword, setRememberPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
  const isLoginEnabled = username.trim().length > 0 && password.trim().length > 0 && !isLoginSubmitting;

  useEffect(() => {
    const rememberedCredentials = window.localStorage.getItem(REMEMBERED_CREDENTIALS_STORAGE_KEY);

    if (!rememberedCredentials) {
      return;
    }

    try {
      const parsedCredentials = JSON.parse(rememberedCredentials);

      if (typeof parsedCredentials.username === 'string' && typeof parsedCredentials.password === 'string') {
        setUsername(parsedCredentials.username);
        setPassword(parsedCredentials.password);
        setRememberPassword(true);
      }
    } catch {
      window.localStorage.removeItem(REMEMBERED_CREDENTIALS_STORAGE_KEY);
    }
  }, []);

  const handlePasswordToggle = () => {
    setIsPasswordHoverLocked(true);
    setIsPasswordVisible((currentValue) => !currentValue);
  };

  const updateRememberedCredentials = () => {
    if (rememberPassword) {
      window.localStorage.setItem(
        REMEMBERED_CREDENTIALS_STORAGE_KEY,
        JSON.stringify({
          username: username.trim(),
          password,
        })
      );
      return;
    }

    window.localStorage.removeItem(REMEMBERED_CREDENTIALS_STORAGE_KEY);
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();

    if (!isLoginEnabled) {
      return;
    }

    setIsLoginSubmitting(true);
    setLoginError('');

    try {
      const session = await authenticateOrquestadorUser(username, password);
      const access = getAccessLevelFromGroups(session.groups);
      const nextSession = {
        ...session,
        access,
      };
      const defaultRoute = getDefaultRouteForSession(nextSession);

      if (!defaultRoute) {
        setLoginError('Tu usuario no tiene permisos asignados para acceder a la aplicación.');
        return;
      }

      updateRememberedCredentials();
      storeOrquestadorSession(nextSession);
      markOrquestadorSessionValidatedForCurrentPageLoad();
      const hasCachedUpdatedAt = Boolean(getCachedOrquestadorUpdatedAtLabel());
      const updatedAtPromise = fetchOrquestadorUpdatedAtLabel();

      if (!hasCachedUpdatedAt) {
        await updatedAtPromise;
      }

      history.push(defaultRoute);
    } catch (error) {
      if (error.status && error.status !== 200) {
        setLoginError('Los datos de usuario o contraseña son incorrectos');
      } else {
        setLoginError('No se ha podido iniciar sesión. Inténtalo de nuevo en unos minutos.');
      }
    } finally {
      setIsLoginSubmitting(false);
    }
  };

  return (
    <main className="login-page" aria-labelledby="login-title">
      <img className="login-brand-logo" src="/assets/branding/mahou-san-miguel.png" alt="Mahou San Miguel" />

      <section className="login-panel">
        <img className="login-steel-logo" src="/assets/branding/steel-gris.png" alt="Steel Logistic Project" />

        <h1 id="login-title" className="login-title">
          <span className="login-title__greeting">¡Hola!</span>
          <span className="login-title__message">Qué bueno tenerte por aquí</span>
        </h1>

        <form className="login-form" onSubmit={handleLoginSubmit}>
          <label className="login-field">
            <span className="login-label">Usuario</span>
            <input
              className="login-input"
              type="text"
              autoComplete="username"
              maxLength="100"
              value={username}
              onChange={(event) => {
                setUsername(event.target.value);
                setLoginError('');
              }}
            />
          </label>

          <label className="login-field">
            <span className="login-label">Contraseña</span>
            <span className="login-password-control">
              <input
                className="login-input login-input--password"
                type={isPasswordVisible ? 'text' : 'password'}
                autoComplete="current-password"
                maxLength="50"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setLoginError('');
                }}
              />
              <button
                className={`login-password-toggle ${isPasswordVisible ? 'login-password-toggle--visible' : ''} ${isPasswordHoverLocked ? 'login-password-toggle--hover-locked' : ''}`}
                type="button"
                aria-label={isPasswordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                aria-pressed={isPasswordVisible}
                onClick={handlePasswordToggle}
                onMouseLeave={() => setIsPasswordHoverLocked(false)}
                onBlur={() => setIsPasswordHoverLocked(false)}
              >
                <img
                  className="login-password-toggle__icon login-password-toggle__icon--closed"
                  src="/assets/icons/eye-closed.svg"
                  alt=""
                  aria-hidden="true"
                />
                <img
                  className="login-password-toggle__icon login-password-toggle__icon--open"
                  src="/assets/icons/eye-open.svg"
                  alt=""
                  aria-hidden="true"
                />
              </button>
            </span>
          </label>

          <label className="login-checkbox-field">
            <input
              className="login-checkbox"
              type="checkbox"
              checked={rememberPassword}
              onChange={(event) => setRememberPassword(event.target.checked)}
            />
            <span className="login-checkbox-text">Recordar contraseña</span>
          </label>

          <button className="login-submit" type="submit" disabled={!isLoginEnabled}>
            {isLoginSubmitting ? 'Validando...' : 'Iniciar sesión'}
          </button>

          {loginError && (
            <p className="login-error" role="alert">
              <svg className="login-error__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7.5v6" />
                <path d="M12 16.75h.01" />
              </svg>
              <span>{loginError}</span>
            </p>
          )}
        </form>
      </section>

      <div className="login-powered-by" aria-label="Powered by VPO">
        <span className="login-powered-by__text">POWERED BY</span>
        <img className="login-powered-by__logo" src="/assets/branding/vpo-naranja.png" alt="VPO" />
      </div>
    </main>
  );
};

export default Login;
